import { useState } from 'react';
import { Plus, KeyRound, Copy, Check } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import { useUserStore } from '../../zustand/UserStore';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../../utils/config';
import type { NewUser, UserType } from '../../types/settings';

interface AddUserFormProps {
  onUserAdded: (user: UserType) => void;
}

export default function AddUserForm({ onUserAdded }: AddUserFormProps) {
  const darkMode = useTheme((state) => state.theme);
  const currentUser = useUserStore((state) => state.user);
  
  const [newUser, setNewUser] = useState<NewUser>({ 
    email: '', 
    role: 'User', 
    name: '', 
    designation: '' 
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);

  // Dynamic roles based on current user's permissions
  const getAvailableRoles = () => {
    if (!currentUser) return [];
    
    const currentUserRole = currentUser.role?.toLowerCase();
    const currentUserDesignation = currentUser.designation?.toLowerCase();
    
    // Super Admin can create Admins and Users
    if (currentUserRole?.includes('super') || currentUserDesignation?.includes('super') ||
        currentUserRole === 'super admin' || currentUserDesignation === 'super admin') {
      return ['Admin', 'User'];
    }
    
    // Admin can only create Users
    if (currentUserRole?.includes('admin') || currentUserDesignation?.includes('admin') ||
        currentUserRole === 'admin' || currentUserDesignation === 'admin') {
      return ['User'];
    }
    
    // Regular users cannot create any users
    return [];
  };

  const roles = getAvailableRoles();

  // Don't render if user doesn't have permission
  if (roles.length === 0) {
    return null;
  }

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setGeneratedPassword(password);
    setCopiedPassword(false);
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const addUser = async () => {
    setAddUserError('');
    if (newUser.email && newUser.name && newUser.designation && generatedPassword) {
      setAddUserLoading(true);
      try {
        const body = {
          email: newUser.email,
          password: generatedPassword,
          name: newUser.name,
          role: newUser.role,
          designation: newUser.designation
        };

        const res = await axios.post(`${getApiBaseUrl()}/api/v1/auth/signup`, body, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (res.status === 200 || res.status === 201) {
          // Create new user object from API response or use provided data
          const newUserData: UserType = {
            uid: res.data?.user?.uid || res.data?.uid || Math.random().toString(36).slice(2),
            name: newUser.name,
            email: newUser.email,
            role: newUser.role,
            status: 'unsuspended' as const,
            designation: newUser.designation,
            head: currentUser?.uid, // Set the current user as the head for hierarchical management
            created_at: new Date().toISOString(),
            email_verified: false
          };

          // Notify parent component
          onUserAdded(newUserData);
          
          // Reset form
          setNewUser({ email: '', role: 'User', name: '', designation: '' });
          setGeneratedPassword('');
          setShowAddUser(false);
          toast.success("User added successfully");
        } else {
          setAddUserError('Failed to add user.');
          toast.error("Failed to add user");
        }
      } catch (err) {
        setAddUserError('Failed to add user.');
        toast.error("Failed to add user");
      } finally {
        setAddUserLoading(false);
      }
    }
  };

  return (
    <div className={`rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 
      transition-all duration-300 hover:shadow-2xl hover:scale-[1.01] ${
      darkMode 
        ? 'bg-[#0a1414] border border-[#1a2424] hover:border-[#1a9f6b]/30' 
        : 'bg-white border border-gray-200 hover:border-[#1a9f6b]/30'
    }`}>
      <div 
        onClick={!showAddUser ? () => setShowAddUser(true) : undefined}
        className={`flex items-center justify-between mb-4 ${
          !showAddUser 
            ? 'cursor-pointer transition-all duration-300 rounded-lg p-2 -m-2' 
            : ''
        } ${
          !showAddUser && darkMode 
            ? 'hover:bg-[#0d1a1a]' 
            : !showAddUser 
            ? 'hover:bg-gray-50' 
            : ''
        }`}
      >
        <h2 className={`text-lg sm:text-xl font-semibold ${
          darkMode ? 'text-white' : 'text-[#053333]'
        }`}>Add New User</h2>
        {showAddUser && (
          <button
            onClick={() => setShowAddUser(false)}
            className={`p-2 rounded-lg transition-all duration-300 ${
              darkMode
                ? 'text-[#1a9f6b] hover:bg-[#050a0a] hover:rotate-90'
                : 'text-[#1a9f6b] hover:bg-gray-100 hover:rotate-90'
            }`}
          >
            <Plus className="w-5 h-5 transform rotate-45 transition-transform duration-300" />
          </button>
        )}
        {!showAddUser && (
          <div className={`p-2 rounded-lg ${
            darkMode ? 'text-[#1a9f6b]' : 'text-[#1a9f6b]'
          }`}>
            <Plus className="w-5 h-5 transition-transform duration-300" />
          </div>
        )}
      </div>

      <div className={`space-y-4 overflow-hidden transition-all duration-300 ${
        showAddUser ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${
              darkMode ? 'text-gray-300' : 'text-[#373737]'
            }`}>
              Email Address
            </label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="user@company.com"
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg 
                focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent
                text-sm sm:text-base transition-all ${
                  darkMode
                    ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
              }`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1.5 ${
              darkMode ? 'text-gray-300' : 'text-[#373737]'
            }`}>
              Role
            </label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg 
                focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent
                text-sm sm:text-base transition-all ${
                  darkMode
                    ? 'bg-[#050a0a] border-[#1a2424] text-white'
                    : 'bg-white border-gray-300 text-[#053333]'
              }`}
            >
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>

        <div className='flex justify-between gap-3'>
          <div className='w-[28%]'>
            <label className={`block text-sm font-medium mb-1.5 ${
              darkMode ? 'text-gray-300' : 'text-[#373737]'
            }`}>
              Full Name
            </label>
            <input
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              placeholder="John Doe"
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg 
                focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent
                text-sm sm:text-base transition-all ${
                  darkMode
                    ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
              }`}
            />
          </div>
          <div className='w-[32%]'>
            <label className={`block text-sm font-medium mb-1.5 ${
              darkMode ? 'text-gray-300' : 'text-[#373737]'
            }`}>
              Designation
            </label>
            <input
              type="text"
              value={newUser.designation}
              onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
              placeholder="e.g. Sales Manager"
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg 
                focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent
                text-sm sm:text-base transition-all ${
                  darkMode
                    ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
              }`}
            />
          </div>
          <div className='w-[36%]'>
            <label className={`block text-sm font-medium mb-1.5 ${
              darkMode ? 'text-gray-300' : 'text-[#373737]'
            }`}>
              Generated Password
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={generatedPassword}
                  readOnly
                  placeholder="Click generate to create password"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 border rounded-lg 
                    text-sm sm:text-base ${
                      darkMode
                        ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500'
                        : 'bg-gray-50 border-gray-300 text-[#053333] placeholder-gray-400'
                  }`}
                />
                {generatedPassword && (
                  <button
                    onClick={copyPassword}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 
                      transition-colors ${
                        darkMode
                          ? 'text-gray-400 hover:text-[#1a9f6b]'
                          : 'text-gray-500 hover:text-[#1a9f6b]'
                    }`}
                  >
                    {copiedPassword ? (
                      <Check className="w-4 h-4 text-[#1a9f6b]" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              <button
                onClick={generatePassword}
                className="px-4 py-2 bg-gradient-to-r from-[#1a9f6b] to-[#148856] 
                  text-white rounded-lg hover:from-[#148856] hover:to-[#0f6644] 
                  transition-all transform active:scale-95 text-sm sm:text-base
                  flex items-center gap-2"
              >
                <KeyRound className="w-4 h-4" />
                Generate
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {addUserError && (
            <span className="text-red-500 text-xs mb-1">{addUserError}</span>
          )}
          <button
            onClick={addUser}
            disabled={!newUser.email || !generatedPassword || addUserLoading}
            className="px-7 cursor-pointer py-2.5 bg-gradient-to-r from-[#1a9f6b] to-[#148856] 
              text-white rounded-lg hover:from-[#148856] hover:to-[#0f6644] 
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all transform active:scale-95 text-sm sm:text-base"
          >
            {addUserLoading ? 'Adding...' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}
